/**
 * Conference filtering service
 * Filters clients based on conference sponsorship data
 */

import { YEAR_CONFIG } from '../config/yearConfig';

/**
 * Build configured conference years as numbers.
 * @returns {number[]}
 */
export const getConfiguredConferenceFilterYears = () => {
  const start = Number(YEAR_CONFIG.baseYear);
  const end = Number(YEAR_CONFIG.lastDataYear);

  if (!Number.isInteger(start) || !Number.isInteger(end) || start > end) {
    return [];
  }

  const years = [];
  for (let year = start; year <= end; year++) {
    years.push(year);
  }
  return years;
};

/**
 * Get clients that have bookings for a specific conference
 * @param {Array} sponsorData - Sponsor data with conference bookings
 * @param {string} conferenceName - Name of the conference to filter by
 * @param {string} selectedYear - Selected year ('All' or specific year like '2024')
 * @returns {Set<string>} Set of customer names that match the conference criteria
 */
export const getConferenceMatchingClients = (sponsorData, conferenceName, selectedYear) => {
  // If "All Conferences" is selected, return null to indicate no filtering
  if (!conferenceName || conferenceName === 'All') {
    return null;
  }

  const matchingCustomers = new Set();

  // Filter sponsor data by conference name
  const conferenceRecords = sponsorData.filter(
    record => record.conferenceName === conferenceName
  );

  conferenceRecords.forEach(record => {
    const customerName = record.customerName;
    if (!customerName) return;

    let hasBooking = false;

    if (selectedYear === 'All') {
      // Check if ANY configured year has rooms > 0 OR revenue > 0
      const years = getConfiguredConferenceFilterYears();
      hasBooking = years.some(year => {
        const yearData = record.yearlyData[year];
        if (!yearData) return false;
        return yearData.rooms > 0 || yearData.revenue > 0;
      });
    } else {
      // Check specific year for rooms > 0 OR revenue > 0
      const year = parseInt(selectedYear);
      const yearData = record.yearlyData[year];
      if (yearData) {
        hasBooking = yearData.rooms > 0 || yearData.revenue > 0;
      }
    }

    if (hasBooking) {
      matchingCustomers.add(customerName);
    }
  });

  return matchingCustomers;
};

/**
 * Filter portfolio clients by conference matching
 * @param {Array} clients - Portfolio clients to filter
 * @param {Set<string>|null} matchingCustomers - Set of customer names or null for no filter
 * @returns {Array} Filtered clients
 */
export const filterClientsByConference = (clients, matchingCustomers) => {
  // If no conference filter (null), return all clients
  if (matchingCustomers === null) {
    return clients;
  }

  // Filter clients where client_name matches a name in matchingCustomers
  // Note: client_name comes from the "Customer" column in Excel (mapped in fallbackDataLoader)
  return clients.filter(client => {
    const customerName = client.client_name;
    return customerName && matchingCustomers.has(customerName);
  });
};
